import { Controller, Post, Body } from '@nestjs/common';
import { ApiTags, ApiOperation, ApiResponse } from '@nestjs/swagger';
import { QaqcAnalysisService } from './qaqc-analysis.service';
import { CreateQaqcFibreAnalysisDto, UpdateQaqcFibreAnalysisDto } from './dto/qaqc-fibre-analysis.dto';
import { CreateQaqcFibreLabAnalysisDto, UpdateQaqcFibreLabAnalysisDto } from './dto/qaqc-fibre-lab-analysis.dto';
import { CreateQaqcProductCountsDto, UpdateQaqcProductCountsDto } from './dto/qaqc-products-counts.dto';

import { QaqcApprovalDto } from './dto/qaqc-approval.dto';
import { CommonResponse } from '@gtpl/shared-models/masters';
import { ApplicationExceptionHandler } from '@gtpl/shared-utils/error-messages';
import { QaqcLotReq, QaqcWorkOrderReq } from '@gtpl/shared-services/raw-material-procurement';

@ApiTags('qaqc-analysis')
@Controller('qaqc-analysis')
export class QaqcAnalysisController {
    constructor(
        private readonly qaqcAnalysisService: QaqcAnalysisService,
        private readonly applicationExceptionHandler: ApplicationExceptionHandler
    ) {}

    @Post('/getWorkOrdersForQAQC')
    @ApiOperation({ summary: 'Get work orders for QAQC' })
    @ApiResponse({ status: 200, description: 'Work orders retrieved successfully' })
    async getWorkOrdersForQAQC(@Body() req: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getWorkOrdersForQAQC(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getLotsDropdownForQAQC')
    
    async getLotsDropdownForQAQC(): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getLotsDropdownForQAQC();
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getWorkOrdersDropdownForQAQC')
    
    async getWorkOrdersDropdownForQAQC(): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getWorkOrdersDropdownForQAQC();
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getLotsForQAQC')
    @ApiOperation({ summary: 'Get lots for QAQC' })
    @ApiResponse({ status: 200, description: 'Lots retrieved successfully' })
    async getLotsForQAQC(@Body() req: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getLotsForQAQC(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getLotsForProductCountsQAQC')
    @ApiOperation({ summary: 'Get lots for QAQC' })
    @ApiResponse({ status: 200, description: 'Lots retrieved successfully' })
    async getLotsForProductCountsQAQC(@Body() req: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getLotsForProductCountsQAQC(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/createFibreAnalysis')
    @ApiOperation({ summary: 'Create fibre analysis' })
    @ApiResponse({ status: 201, description: 'Fibre analysis created successfully' })
    async createFibreAnalysis(@Body() createDto: CreateQaqcFibreAnalysisDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.createFibreAnalysis(createDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/createFibreLabAnalysis')
    @ApiOperation({ summary: 'Create fibre lab analysis' })
    @ApiResponse({ status: 201, description: 'Fibre lab analysis created successfully' })
    async createFibreLabAnalysis(@Body() createDto: CreateQaqcFibreLabAnalysisDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.createFibreLabAnalysis(createDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getAllFibreAnalysis')
    @ApiOperation({ summary: 'Get all fibre analysis records' })
    @ApiResponse({ status: 200, description: 'Fibre analysis records retrieved successfully' })
    async getAllFibreAnalysis(@Body() req?: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getAllFibreAnalysis(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getAllFibreLabAnalysis')
    @ApiOperation({ summary: 'Get all fibre lab analysis records' })
    @ApiResponse({ status: 200, description: 'Fibre lab analysis records retrieved successfully' })
    async getAllFibreLabAnalysis(@Body() req?: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getAllFibreLabAnalysis(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getFibreAnalysisByWorkOrder')
    @ApiOperation({ summary: 'Get fibre analysis by work order' })
    @ApiResponse({ status: 200, description: 'Fibre analysis by work order retrieved successfully' })
    async getFibreAnalysisByWorkOrder(@Body() req: { workOrderNo: string }): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getFibreAnalysisByWorkOrder(req.workOrderNo);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getFibreAnalysisByLot')
    @ApiOperation({ summary: 'Get fibre analysis by lot' })
    @ApiResponse({ status: 200, description: 'Fibre analysis by lot retrieved successfully' })
    async getFibreAnalysisByLot(@Body() req: { lotNumber: string }): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getFibreAnalysisByLot(req.lotNumber);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getFibreLabAnalysisByWorkOrder')
    @ApiOperation({ summary: 'Get fibre lab analysis by work order' })
    @ApiResponse({ status: 200, description: 'Fibre lab analysis by work order retrieved successfully' })
    async getFibreLabAnalysisByWorkOrder(@Body() req: { workOrderNo: string }): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getFibreLabAnalysisByWorkOrder(req.workOrderNo);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getFibreLabAnalysisByLot')
    @ApiOperation({ summary: 'Get fibre lab analysis by lot' })
    @ApiResponse({ status: 200, description: 'Fibre lab analysis by lot retrieved successfully' })
    async getFibreLabAnalysisByLot(@Body() req: { lotNumber: string }): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getFibreLabAnalysisByLot(req.lotNumber);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/updateFibreAnalysis')
    @ApiOperation({ summary: 'Update fibre analysis' })
    @ApiResponse({ status: 200, description: 'Fibre analysis updated successfully' })
    async updateFibreAnalysis(@Body() updateDto: UpdateQaqcFibreAnalysisDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.updateFibreAnalysis(updateDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/updateFibreLabAnalysis')
    @ApiOperation({ summary: 'Update fibre lab analysis' })
    @ApiResponse({ status: 200, description: 'Fibre lab analysis updated successfully' })
    async updateFibreLabAnalysis(@Body() updateDto: UpdateQaqcFibreLabAnalysisDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.updateFibreLabAnalysis(updateDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/approveQaqcTest')
    @ApiOperation({ summary: 'Approve QAQC test' })
    @ApiResponse({ status: 200, description: 'QAQC test approved successfully' })
    async approveQaqcTest(@Body() approvalDto: QaqcApprovalDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.approveQaqcTest(approvalDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

 

    @Post('/createProductCounts')
    @ApiOperation({ summary: 'Create product counts' })
    @ApiResponse({ status: 201, description: 'Product counts created successfully' })
    async createProductCounts(@Body() createDto: CreateQaqcProductCountsDto): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.createProductCounts(createDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/getProductCounts')
    @ApiOperation({ summary: 'Get all product counts' })
    @ApiResponse({ status: 200, description: 'Product counts retrieved successfully' })
    async getProductCounts(@Body() req?: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.getProductCounts(req);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/updateProductCounts')
    @ApiOperation({ summary: 'Update product count' })
    @ApiResponse({ status: 200, description: 'Product count updated successfully' })
    async updateProductCounts(@Body() updateDto: any): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.updateProductCounts(updateDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }

    @Post('/approveProductCounts')
    @ApiOperation({ summary: 'Approve product counts' })
    @ApiResponse({ status: 200, description: 'Product counts approved successfully' })
    async approveProductCounts(@Body() approvalDto: { ids: number[] }): Promise<CommonResponse> {
        try {
            return await this.qaqcAnalysisService.approveProductCounts(approvalDto);
        } catch (error) {
            return this.applicationExceptionHandler.returnException(CommonResponse, error);
        }
    }
}


